import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Phone, Mail } from "lucide-react";

const steps = [
  {
    title: "Step 1 — Raise It With Support",
    desc: "Contact our support team with your order ID and a description of the issue. Most concerns are resolved at this stage.",
  },
  {
    title: "Step 2 — Escalation",
    desc: "If you're not satisfied with the response, ask to escalate — your complaint is reviewed by a senior member of the support team.",
  },
  {
    title: "Step 3 — Final Review",
    desc: "Unresolved escalations are reviewed by the Grievance Officer for a final decision, communicated back to you in writing.",
  },
];

const GrievanceRedressalPolicy = () => {
  const navigate = useNavigate();

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.breadcrumb}>Home &nbsp;›&nbsp; Grievance Redressal Policy</div>

        <h1 style={styles.title}>Grievance Redressal Policy</h1>
        <p style={styles.intro}>
          If something goes wrong with a booking, payment, or your experience on
          ClickNido, here's how to raise it and what happens next.
        </p>

        <div style={styles.card}>
          <h2 style={styles.cardTitle}>How Complaints Are Handled</h2>
          <div style={styles.stepsGrid}>
            {steps.map((s) => (
              <div key={s.title} style={styles.stepCard}>
                <div style={styles.stepTitle}>{s.title}</div>
                <div style={styles.stepDesc}>{s.desc}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Response Timelines</h2>
          <ul style={styles.list}>
            <li>Acknowledgement of your complaint: within 48 hours.</li>
            <li>Resolution or a clear update: within 7 business days for most issues.</li>
            <li>Complex cases (e.g. involving payment reconciliation) may take longer — we'll keep you informed of progress.</li>
          </ul>
        </div>

        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Grievance Officer</h2>
          <p style={styles.contactText}>
            For unresolved complaints, you can reach our Grievance Officer through the
            same support channels below — please mention that your query is a
            grievance escalation.
          </p>
          <div style={styles.contactRow}>
            <Phone size={16} color="#6d5efc" />
            <span>+91 98765 43210</span>
          </div>
          <div style={styles.contactRow}>
            <Mail size={16} color="#6d5efc" />
            <span>support@servicehub.com</span>
          </div>
          <button style={{ ...styles.inlineLink, marginTop: "16px" }} onClick={() => navigate("/contact-us")}>
            Contact Us <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  page: {
    background: "#f5f6fb",
    minHeight: "100vh",
    padding: "40px 16px",
    fontFamily:
      "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    color: "#111827",
  },
  container: { maxWidth: "860px", margin: "0 auto" },
  breadcrumb: { color: "#7b8198", fontSize: "13px", fontWeight: 600, marginBottom: "14px" },
  title: { margin: 0, fontSize: "32px", fontWeight: 800, color: "#1c2148", marginBottom: "12px" },
  intro: { margin: "0 0 28px", fontSize: "15px", lineHeight: 1.75, color: "#5c6480", maxWidth: "700px" },
  card: {
    background: "#fff",
    borderRadius: "20px",
    padding: "26px",
    boxShadow: "0 1px 2px rgba(16,24,40,0.04), 0 8px 28px rgba(16,24,40,0.04)",
    marginBottom: "18px",
  },
  cardTitle: { margin: "0 0 14px", fontSize: "17px", fontWeight: 800, color: "#21284d" },
  list: { margin: 0, paddingLeft: "20px", color: "#5c6480", fontSize: "14px", lineHeight: 1.9 },
  stepsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "14px" },
  stepCard: { background: "#f8f8fe", borderRadius: "16px", padding: "16px" },
  stepTitle: { fontSize: "13px", fontWeight: 800, color: "#3c4462", marginBottom: "6px" },
  stepDesc: { fontSize: "13px", color: "#6f7892", lineHeight: 1.7 },
  contactText: { margin: "0 0 14px", fontSize: "14px", lineHeight: 1.8, color: "#5c6480" },
  contactRow: { display: "flex", alignItems: "center", gap: "10px", fontSize: "14px", color: "#374151", marginBottom: "8px" },
  inlineLink: {
    background: "transparent",
    border: "none",
    color: "#6d5efc",
    fontWeight: 700,
    fontSize: "14px",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    cursor: "pointer",
    padding: 0,
  },
};

export default GrievanceRedressalPolicy;
